import type {
  PageContext,
  StepListener,
  StepPageBlock,
  UIEventBinding,
  UITrigger,
} from '@template-project/funnels-contract';
import {
  getFunnelEventSemantic,
  normalizeEventName,
} from '@trafilea/tss-lib-funnel-schema-ts';
import type { FunnelEventBus } from './funnel-event-bus';
import { createEventEnvelope, type UIEventPayload } from './envelope';
import { evaluateStepHook, resolveEventParams } from './params-from-resolver';
import { isFunnelUiEventsEnabled } from './feature-flag';
import { matchesListenerSource } from './listener-source-correlation';

export interface EmitBlockEventOptions {
  bus: FunnelEventBus;
  binding: UIEventBinding;
  trigger: UITrigger;
  block: StepPageBlock;
  pageContext: Partial<PageContext>;
  sessionId: string;
  tenantId: string;
  storeId?: string;
  funnelId?: string;
  stepId?: string;
  variantKey?: string;
  value?: unknown;
  eventMeta?: Record<string, unknown>;
}

export function emitBlockEvent(options: EmitBlockEventOptions): void {
  if (!isFunnelUiEventsEnabled()) return;

  const {
    bus,
    binding,
    trigger,
    block,
    pageContext,
    sessionId,
    tenantId,
    storeId,
    funnelId,
    stepId,
    variantKey,
    value,
    eventMeta,
  } = options;

  if (binding.condition && !evaluateStepHook(binding.condition, pageContext)) {
    return;
  }

  const params = resolveEventParams(binding.params, binding.paramsFrom, {
    value,
    blockProps: block.props,
    pageContext,
    eventMeta,
  });

  const payload: UIEventPayload = {
    params,
    context: {
      funnelId,
      stepId,
      blockId: block.id,
      blockType: block.type,
      trigger,
      pageType: pageContext.pageType,
      currentUrl:
        pageContext.url ??
        (typeof window !== 'undefined' ? window.location.href : undefined),
      variantKey,
    },
  };

  const eventName = normalizeEventName(binding.name);
  const semantic = getFunnelEventSemantic(eventName);

  // A browser-authored binding cannot impersonate an order/payment/domain
  // authority. Those results enter the bus only from their trusted adapters.
  if (semantic?.kind === 'authoritative_result') {
    return;
  }

  bus.emit(
    createEventEnvelope(eventName, 'storefront-funnel', payload, {
      phase:
        semantic?.kind === 'command_result' ||
        semantic?.kind === 'component_result'
          ? 'intent'
          : 'observation',
      origin: 'block',
      sessionId,
      scope: { tenantId, storeId },
    }),
  );
}

export interface RegisterStepListenersOptions {
  bus: FunnelEventBus;
  listeners: StepListener[];
  onReaction?: (listener: StepListener, envelope: ReturnType<typeof createEventEnvelope>) => void;
}

export function registerStepListeners(
  options: RegisterStepListenersOptions,
): Array<{ unsubscribe: () => void }> {
  const subs = options.listeners.map((listener) =>
    options.bus.on(normalizeEventName(listener.eventName), (envelope) => {
      if (!matchesListenerSource(listener, envelope)) return;
      options.onReaction?.(listener, envelope);
    }),
  );
  return subs;
}
